import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Send, Loader2, Briefcase, RotateCcw } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

// The candidate's conversation with VetraHub. One ongoing thread — a message can carry an
// application, rendered as a job card so "which job?" survives a single flat thread.
//
// IN-APP ONLY by design: VetraHub never emails candidates its own notifications, and these must
// never mix with the managed employer inbox, which carries real employer mail.

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 604800) return `${Math.floor(s / 86400)}d`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

// When we say "we've fixed it, try again", the job has to be one click away — otherwise the
// candidate has to go and find it, and an application that is ready to work stays unsent.
function JobCard({ job, onRetried }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  if (!job) return null;
  const retry = async () => {
    setBusy(true);
    try {
      await api.post(`/apply/draft/${job.draft_id}/retry`);
      toast.success("Trying this application again.");
      onRetried && onRetried();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start it again");
    } finally { setBusy(false); }
  };
  return (
    <div className="mt-2 rounded-lg border border-border bg-white/70 p-2.5">
      <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground mb-0.5">
        <Briefcase className="w-3 h-3 flex-shrink-0" />
        <span className="truncate">{job.company || "Application"}{job.title ? ` · ${job.title}` : ""}</span>
        <span className="ml-auto font-mono text-[10px] flex-shrink-0">{job.status}</span>
      </div>
      <div className="flex items-center gap-2 mt-1.5">
        <button onClick={() => navigate(`/apply/status/${job.draft_id}`)}
          className="text-[11px] font-semibold text-[#0025D8] hover:underline">Open</button>
        {job.retryable && (
          <button onClick={retry} disabled={busy}
            className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded border border-[#0025D8]/30 text-[#0025D8] hover:bg-[#0025D8]/[0.05] disabled:opacity-40">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />} Try again
          </button>
        )}
        {job.applied_manually && (
          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700">
            you applied manually
          </span>
        )}
      </div>
    </div>
  );
}

export default function SupportChat({ compact = false, draftId = null, onUnread }) {
  const [data, setData] = useState(null);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const load = useCallback(async (markRead = true) => {
    try {
      const { data: d } = await api.get("/support/messages");
      setData(d);
      onUnread && onUnread(d.unread || 0);
      if (markRead && d.unread) {
        await api.post("/support/messages/read");
        onUnread && onUnread(0);
      }
    } catch { setData({ messages: [], jobs: {} }); }
  }, [onUnread]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [data?.messages?.length]);

  const send = async () => {
    const text = body.trim();
    if (!text) return;
    setSending(true);
    try {
      await api.post("/support/messages", { body: text, draft_id: draftId || undefined });
      setBody("");
      await load(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send your message");
    } finally { setSending(false); }
  };

  const msgs = data?.messages || [];
  return (
    <div className="flex flex-col min-h-0 h-full">
      <div className={`flex-1 overflow-y-auto px-3 py-3 space-y-3 ${compact ? "max-h-[320px]" : ""}`}>
        {!data && <div className="flex items-center gap-2 text-sm text-muted-foreground py-4"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>}
        {data && !msgs.length && (
          <div className="text-center py-8">
            <div className="text-sm font-semibold text-[#0F172A]">No messages yet</div>
            <p className="text-[12px] text-muted-foreground mt-1 leading-relaxed max-w-[280px] mx-auto">
              Stuck on something, or an application keeps failing? Tell us here and we'll look into it.
            </p>
          </div>
        )}
        {msgs.map((m) => {
          const mine = m.sender === "candidate";
          return (
            <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-2xl px-3 py-2 ${
                mine ? "bg-[#0025D8] text-white" : "bg-slate-100 text-[#0F172A]"}`}>
                {!mine && <div className="text-[10px] font-bold opacity-60 mb-0.5">VetraHub</div>}
                <div className="text-[13px] whitespace-pre-wrap leading-relaxed">{m.body}</div>
                {m.draft_id && <JobCard job={(data.jobs || {})[m.draft_id]} onRetried={() => load(false)} />}
                <div className={`text-[10px] mt-1 ${mine ? "text-white/60" : "text-muted-foreground"}`}>{timeAgo(m.created_at)}</div>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <div className="border-t border-border p-2.5 flex items-end gap-2 flex-shrink-0">
        <textarea
          value={body} onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          rows={compact ? 2 : 3} placeholder="Tell us what's happening…"
          className="flex-1 resize-none rounded-lg border border-border px-2.5 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#0025D8]/20" />
        <button onClick={send} disabled={sending || !body.trim()}
          className="h-9 w-9 grid place-items-center rounded-lg bg-[#0025D8] text-white disabled:opacity-40 flex-shrink-0">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}
